import { ApiRequestError, isNetworkFailure } from '../shared/api/errors';
import { apiRequestDirect } from '../shared/api/http';
import { deviceHasNetwork } from './connectivity';
import { emitOffline } from './events';
import {
  listOutbox,
  openOutboxBody,
  type OutboxRecord,
  putOutbox,
  removeOutbox,
} from './outbox';
import { offlineStore } from './store';
import { vaultUnlocked } from './vault';

/**
 * Replaying the queue.
 *
 * The engine is strictly serial and stops at the first item it cannot finish. That is not
 * timidity: the queue is ordered, and a draft's later edit and submission address a document the
 * earlier item was supposed to create. Racing ahead past a refused create would send edits for
 * something that does not exist, and would bury the one failure a person needs to see.
 */

const SYNC_STATE_KEY = 'sync-state';

/** Grows with consecutive server-side failures, so a struggling API is not hammered. */
const BACKOFF_SECONDS = [5, 15, 60, 300, 900];

export type SyncPause =
  /** No connection. Nothing was attempted. */
  | 'offline'
  /** The offline copy is sealed, so the queue cannot be read. */
  | 'sealed'
  /** The server does not accept this session any more; a real sign-in is needed. */
  | 'authentication'
  /** The server refused an item on its merits, and a person has to decide what happens. */
  | 'rejected'
  /** The server could not answer. It will be tried again. */
  | 'server'
  /** An item was dispatched and the reply was lost; a person has to confirm the outcome. */
  | 'uncertain';

export interface SyncResult {
  readonly startedAt: string;
  readonly finishedAt: string;
  readonly sent: number;
  readonly remaining: number;
  readonly pausedBy: SyncPause | null;
}

export interface SyncState {
  readonly lastRunAt: string | null;
  readonly lastSuccessAt: string | null;
  readonly lastResult: SyncResult | null;
}

const emptyState: SyncState = { lastRunAt: null, lastSuccessAt: null, lastResult: null };

export async function readSyncState(): Promise<SyncState> {
  try {
    return await offlineStore().get<SyncState>('meta', SYNC_STATE_KEY) ?? emptyState;
  } catch {
    return emptyState;
  }
}

async function writeSyncState(state: SyncState): Promise<void> {
  try {
    await offlineStore().put('meta', SYNC_STATE_KEY, state);
  } catch {
    // Losing the "last synced" note is cosmetic; the queue itself is what matters.
  }
}

function backoffUntil(attempts: number): string {
  const seconds = BACKOFF_SECONDS[Math.min(attempts, BACKOFF_SECONDS.length - 1)] ?? 900;
  return new Date(Date.now() + seconds * 1000).toISOString();
}

/** An item is ready when it is waiting to be sent and its backoff has elapsed. */
function isReady(record: OutboxRecord, now: number): boolean {
  if (record.state !== 'pending' && record.state !== 'failed') return false;
  return record.nextAttemptAt === null || Date.parse(record.nextAttemptAt) <= now;
}

function requestFor(record: OutboxRecord, body: unknown): RequestInit {
  return {
    method: record.method,
    headers: { 'Idempotency-Key': record.idempotencyKey },
    ...(body === undefined || body === null ? {} : { body: JSON.stringify(body) }),
  };
}

let inFlight: Promise<SyncResult> | null = null;

/**
 * Sends everything the device is holding.
 *
 * Concurrent calls share one run — the connectivity listener, the service worker, the periodic
 * timer and the operator's own button all end up here, and two of them at once would double-send.
 */
export function flushOutbox(): Promise<SyncResult> {
  inFlight ??= runFlush().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

export function syncInProgress(): boolean {
  return inFlight !== null;
}

async function runFlush(): Promise<SyncResult> {
  const startedAt = new Date().toISOString();
  const finish = async (sent: number, remaining: number, pausedBy: SyncPause | null): Promise<SyncResult> => {
    const result: SyncResult = { startedAt, finishedAt: new Date().toISOString(), sent, remaining, pausedBy };
    const previous = await readSyncState();
    await writeSyncState({
      lastRunAt: result.finishedAt,
      lastSuccessAt: sent > 0 || (remaining === 0 && pausedBy === null) ? result.finishedAt : previous.lastSuccessAt,
      lastResult: result,
    });
    emitOffline('queue-changed');
    emitOffline('sync-changed');
    return result;
  };

  emitOffline('sync-changed');

  /*
   * The gate is the device's own network, not the workspace's belief about the server. Sending is
   * how that belief is corrected, so refusing to send while it says "unreachable" would leave a
   * queue that could never drain after a single spurious failure. With the interface genuinely
   * down there is no point trying, and that is the case this skips.
   */
  if (!deviceHasNetwork()) return finish(0, (await listOutbox()).length, 'offline');
  if (!vaultUnlocked()) return finish(0, (await listOutbox()).length, 'sealed');

  const queue = await listOutbox();
  const now = Date.now();
  let sent = 0;

  for (const record of queue) {
    if (!isReady(record, now)) continue;

    const body = await openOutboxBody(record);
    if (record.sealedBody !== null && body === null) {
      // Sealed under a data key this device no longer has. It can never be sent, so it is put in
      // front of a person rather than retried forever.
      await putOutbox({ ...record, state: 'rejected', lastError: { status: 0, code: 'unreadable' } });
      return finish(sent, remaining(await listOutbox()), 'rejected');
    }

    const attempted: OutboxRecord = {
      ...record,
      attempts: record.attempts + 1,
      lastAttemptAt: new Date().toISOString(),
    };

    try {
      await apiRequestDirect(record.path, requestFor(record, body));
      await removeOutbox(record.id);
      sent += 1;
      emitOffline('queue-changed');
      continue;
    } catch (error) {
      if (isNetworkFailure(error)) {
        // Did it leave the device? If the interface itself has gone down, almost certainly not,
        // and the item stays queued. If the interface is still up, the request may have been
        // received and only the reply lost — which no retry can distinguish, so it is handed to
        // a person instead of being sent a second time.
        const interfaceDown = typeof navigator !== 'undefined' && navigator.onLine === false;
        await putOutbox(interfaceDown
          ? { ...attempted, state: 'pending', nextAttemptAt: null }
          : { ...attempted, state: 'uncertain', lastError: { status: 0, code: 'no_reply' } });
        return finish(sent, remaining(await listOutbox()), interfaceDown ? 'offline' : 'uncertain');
      }

      if (!(error instanceof ApiRequestError)) {
        await putOutbox({ ...attempted, state: 'failed', nextAttemptAt: backoffUntil(attempted.attempts), lastError: { status: 0, code: null } });
        return finish(sent, remaining(await listOutbox()), 'server');
      }

      const lastError = { status: error.status, code: error.code ?? null };

      if (error.status === 401 || error.status === 403) {
        // Not the item's fault. It keeps its place, and the operator is asked to sign in.
        await putOutbox({ ...attempted, state: 'pending', nextAttemptAt: null, lastError });
        return finish(sent, remaining(await listOutbox()), 'authentication');
      }

      if (error.status >= 500) {
        await putOutbox({ ...attempted, state: 'failed', nextAttemptAt: backoffUntil(attempted.attempts), lastError });
        return finish(sent, remaining(await listOutbox()), 'server');
      }

      await putOutbox({ ...attempted, state: 'rejected', nextAttemptAt: null, lastError });
      return finish(sent, remaining(await listOutbox()), 'rejected');
    }
  }

  return finish(sent, remaining(await listOutbox()), null);
}

function remaining(queue: readonly OutboxRecord[]): number {
  return queue.length;
}

/** Puts a refused or unresolved item back in the queue for another attempt. */
export async function retryQueuedWrite(id: string): Promise<void> {
  const queue = await listOutbox();
  const record = queue.find((item) => item.id === id);
  if (record === undefined) return;
  await putOutbox({ ...record, state: 'pending', nextAttemptAt: null, lastError: null });
  emitOffline('queue-changed');
}

/** Removes an item without sending it. The work it described is lost, so the interface confirms. */
export async function discardQueuedWrite(id: string): Promise<void> {
  await removeOutbox(id);
  emitOffline('queue-changed');
}
